import { IonIcon } from '@ionic/react'
import { arrowUpOutline, calendarOutline, logoWhatsapp, warningOutline } from 'ionicons/icons'
import React, { useEffect } from 'react'
import useAxios from 'axios-hooks'
import styled from '@emotion/styled'
import { addDays, format } from 'date-fns'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'

import { Show } from '../common/Show'
import { useCursoAtual } from '../cursos/CursosStore'
import { MatriculaModel, STATUS_MATRICULA } from '../models/MatriculaModel'
import { CursoModel } from '../models/CursoModel'
import { useOpenJivoChat } from '../common/hooks/useJivoApi'
import { useDecorateBlobWithSas } from '../common/hooks/useDecorateBlobWithSas'
import DownloadSvg from '../svgs/download'
import { DownloadModel } from '../models/DownloadModel'
import { AUTH_COLORS } from '../auth/AuthForm'
import { getUsuarioLogado } from '../auth/AuthStore'

type CourseStatusTone = 'success' | 'info' | 'error'

const COURSE_STATUS_CARD_COLORS: Record<CourseStatusTone, string> = {
  success: 'rgba(39, 174, 96, 1)',
  info: 'rgba(47, 128, 237, 1)',
  error: 'rgba(234, 67, 53, 1)',
}

const CourseStatusCard = ({
  tone = 'info',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { tone?: CourseStatusTone }) => {
  const { Layout } = CourseStatusCard
  return <Layout tone={tone} {...props} />
}

CourseStatusCard.Layout = styled.div<{ tone?: CourseStatusTone }>`
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: 1px solid rgba(209, 215, 224, 1);
  border-top: 8px solid ${(props) => COURSE_STATUS_CARD_COLORS[props.tone ?? 'info']};
  border-radius: 8px;
  padding: 23px 18px 21px;
  background: #ffffff;
  color: ${AUTH_COLORS.blue};
  box-shadow: 0 4px 16px rgba(51, 51, 51, 0.08);

  @media (max-width: 768px) {
    padding: 20px 14px 18px;
  }
`

CourseStatusCard.Title = styled.h2`
  margin: 0;
  color: ${AUTH_COLORS.blue};
  font-size: 21px;
  font-weight: 700;
  line-height: 21px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 20px;
  }
`

CourseStatusCard.Description = styled.p`
  margin: 12px 0 0;
  color: rgba(18, 42, 65, 0.66);
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;

  @media (max-width: 768px) {
    margin: 8px 0 0;
  }
`

CourseStatusCard.Panel = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  border: 1px solid rgba(209, 215, 224, 1);
  border-radius: 8px;
  padding: 16px 18px;

  @media (max-width: 768px) {
    padding: 14px 16px;
  }

  p {
    margin: 4px 0 0;
    color: ${AUTH_COLORS.blue};
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
  }
`

CourseStatusCard.PanelHeader = styled.h3`
  margin: 0;
  color: ${AUTH_COLORS.blue};
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
`

CourseStatusCard.ProgressRow = styled.div`
  margin-top: 4px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;

  span {
    color: ${AUTH_COLORS.blue};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
`

CourseStatusCard.ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(232, 235, 241, 1);
`

CourseStatusCard.ProgressFill = styled.div<{ tone?: CourseStatusTone }>`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: ${(props) => COURSE_STATUS_CARD_COLORS[props.tone ?? 'success']};
`

CourseStatusCard.Footnote = styled.p`
  margin: 12px 0 0;
  color: rgba(130, 130, 130, 1);
  font-size: 11px;
  font-style: italic;
  font-weight: 400;
  line-height: 12px;
`

export const Certificado = ({ matricula }: { matricula: MatriculaModel }) => {
  const curso = useCursoAtual()

  const [{ data, loading, error }] = useAxios<MatriculaModel>(
    `/matriculas/certificado?cursoId=${curso?.id}`
  )

  if (loading) {
    return <CertificadoLoadingCard />
  }

  if (error && matricula?.statusDetran !== STATUS_MATRICULA.FINALIZADO) {
    const prazoPendente = getPrazoPendenteInfo({
      curso,
      error,
    })

    if (prazoPendente) return <CertificadoAguardandoPrazoCard prazo={prazoPendente} />

    return <CertificadoErroCard matricula={matricula} error={error} />
  }

  return <CertificadoLiberadoCard matricula={data ?? matricula} />
}

function CertificadoLoadingCard() {
  const { Title, Description, Panel, PanelHeader, ProgressRow, ProgressTrack } = CourseStatusCard
  const { ProgressFill, LoadingText } = CertificadoLoadingCard

  return (
    <CourseStatusCard tone="info">
      <Title>Buscando dados do certificado</Title>
      <Description>
        Estamos verificando as informações do seu curso para carregar o status do certificado.
      </Description>

      <Panel>
        <PanelHeader>Status do certificado</PanelHeader>
        <ProgressRow>
          <ProgressTrack>
            <ProgressFill />
          </ProgressTrack>
          <span>Carregando</span>
        </ProgressRow>
        <LoadingText>Aguarde alguns instantes.</LoadingText>
      </Panel>
    </CourseStatusCard>
  )
}

CertificadoLoadingCard.ProgressFill = styled(CourseStatusCard.ProgressFill)`
  width: 42%;
  background: ${COURSE_STATUS_CARD_COLORS.info};
  animation: certificado-loading 1.2s ease-in-out infinite;

  @keyframes certificado-loading {
    0% {
      transform: translateX(-110%);
    }

    100% {
      transform: translateX(245%);
    }
  }
`

CertificadoLoadingCard.LoadingText = styled.p`
  margin: 8px 0 0;
`

function checkCursoNecessitaProvaDetranCursoEspecializado(matricula: MatriculaModel) {
  const cursosDeAtualizacao = [3, 4, 5, 6, 7]
  if (cursosDeAtualizacao.includes(Number(matricula.cursoId))) return true
  return false
}

function checkCursoNecessitaProvaDetranReciclagem(matricula: MatriculaModel) {
  const cursosDeAtualizacao = [1, 18]
  if (cursosDeAtualizacao.includes(Number(matricula.cursoId))) return true
  return false
}

const CertificadoPrimaryButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 14px;
  border: 0;
  border-radius: 4px;
  background: rgba(39, 174, 96, 1);
  color: #ffffff;
  font-family: Rubik, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s ease;

  :hover {
    background: rgba(42, 142, 70, 1);
  }
`

const CertificadoScheduleButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 12px;
  border: 1px solid rgba(217, 217, 217, 1);
  border-radius: 4px;
  background: #ffffff;
  color: rgba(141, 141, 141, 1);
  font-family: Rubik, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    border-color 0.2s ease,
    color 0.2s ease,
    background 0.2s ease;

  ion-icon {
    width: 18px;
    height: 18px;
  }

  :hover {
    border-color: rgba(39, 174, 96, 1);
    color: rgba(39, 174, 96, 1);
    background: rgba(52, 168, 83, 0.06);
  }
`

function CertificadoLiberadoCard({ matricula }: { matricula: MatriculaModel }) {
  const {
    Title,
    Description,
    Panel,
    PanelHeader,
    ProgressRow,
    ProgressTrack,
    ProgressFill,
    Footnote,
  } = CourseStatusCard
  const curso = useCursoAtual()
  const showAgendarProva =
    matricula?.uf === 'PE' &&
    (checkCursoNecessitaProvaDetranCursoEspecializado(matricula) ||
      checkCursoNecessitaProvaDetranReciclagem(matricula))
  const showDetranIntegrationFootnote = curso?.tipoCurso === 'CREDENCIADO_DETRAN'

  function handleClickDownload() {
    if (process.env.NODE_ENV === 'production') {
      window.open(
        `https://app.newdriver.com.br/certificado/${matricula.identificacaoCertificado}`
      )
      return
    }

    window.open(`http://localhost:3001/certificado/${matricula.identificacaoCertificado}`)
  }

  function handleClickAgendarProva() {
    window.open('https://www.detran.pe.gov.br/prova-teorica-de-reciclagem')
  }

  return (
    <CourseStatusCard tone="success">
      <Title>Parabéns pela sua conquista!</Title>
      <Description>
        Você concluiu todas as etapas do curso e cumpriu o prazo mínimo exigido para certificação.
      </Description>

      <Panel>
        <PanelHeader>Certificado liberado</PanelHeader>
        <ProgressRow>
          <ProgressTrack>
            <ProgressFill tone="success" />
          </ProgressTrack>
          <span>100%</span>
        </ProgressRow>
        <p>Seu certificado está pronto para download.</p>

        <CertificadoPrimaryButton type="button" onClick={handleClickDownload}>
          <DownloadSvg width={23} height={23} />
          Baixar certificado
        </CertificadoPrimaryButton>

        <ProjetoPedagogico matricula={matricula} />

        {showAgendarProva ? (
          <CertificadoScheduleButton type="button" onClick={handleClickAgendarProva}>
            <IonIcon icon={calendarOutline} />
            Agendar prova no DETRAN-PE
          </CertificadoScheduleButton>
        ) : null}
      </Panel>

      {showDetranIntegrationFootnote ? (
        <Footnote>
          O que acontece agora? O registro da conclusão seguirá os procedimentos normais de
          integração com os órgãos de trânsito. Caso a informação ainda não apareça imediatamente em
          seu prontuário ou na CNH Digital, não se preocupe. O processamento pelos órgãos de
          trânsito pode levar alguns dias após a emissão do certificado.
        </Footnote>
      ) : null}

    </CourseStatusCard>
  )
}

const CERTIFICADO_WHATSAPP_NUMBER = '556230867360'

function getCertificadoWhatsappUrl({
  matricula,
  errorMessage,
  usuarioLogado,
}: {
  matricula: MatriculaModel
  errorMessage: string
  usuarioLogado?: { nome?: string; cpf?: string }
}) {
  const aluno = usuarioLogado?.nome ? usuarioLogado : matricula?.aluno
  const alunoTexto = aluno?.nome ? `Sou ${aluno.nome}` : 'Sou aluno(a) da New Driver'
  const cpfTexto = aluno?.cpf ? `, CPF ${aluno.cpf}` : ''
  const ufTexto = matricula?.uf ? `, estado ${matricula.uf}` : ''
  const cursoTexto = matricula?.curso?.nome ? `, curso ${matricula.curso.nome}` : ''
  const mensagem = `${alunoTexto}${cpfTexto}${ufTexto}${cursoTexto}. Estou com problema para emitir meu certificado. Motivo: ${errorMessage}`

  return `https://wa.me/${CERTIFICADO_WHATSAPP_NUMBER}?text=${encodeURIComponent(mensagem)}`
}

const CertificadoErroAgendaText = styled.div`
  margin-top: 12px;
  color: rgba(130, 130, 130, 1);
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;

  p {
    margin: 0;
  }

  p + p {
    margin-top: 18px;
  }

  strong {
    font-weight: 600;
  }
`

const CertificadoErroReasonBox = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  border: 1px solid #ea4331;
  border-radius: 8px;
  background: rgba(233, 66, 48, 0.1);
  padding: 16px;
  color: #2d2d2d;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  ion-icon {
    width: 24px;
    height: 24px;
    color: #ea4234;
    --ionicon-stroke-width: 38px;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #2d2d2d;
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #2d2d2d;
    font-size: 18px;
    font-weight: 700;
    line-height: 21px;
    text-transform: uppercase;
    overflow-wrap: anywhere;
  }
`

const CertificadoErroSupportBox = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  border: 1px solid ${AUTH_COLORS.blue};
  border-radius: 6px;
  padding: 24px;
  text-align: center;

  button {
    width: 100%;
    border: 0;
    background: transparent;
    font-family: Rubik, sans-serif;
    cursor: pointer;
  }

  .chat {
    height: 48px;
    border: 1px solid ${AUTH_COLORS.blue};
    border-radius: 6px;
    color: ${AUTH_COLORS.blue};
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition:
      background 0.2s ease,
      color 0.2s ease;
  }

  .chat:hover {
    background: ${AUTH_COLORS.blue};
    color: #ffffff;
  }

  .whatsapp {
    margin-top: 24px;
    color: #10a847;
    font-size: 16px;
    line-height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .whatsapp:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    padding: 23px 12px 24px;
  }
`

function CertificadoErroCard({
  matricula,
  error,
}: {
  matricula: MatriculaModel
  error: AxiosError<any>
}) {
  const { Title, Description } = CourseStatusCard
  const jivo = useOpenJivoChat()
  const usuarioLogado = useSelector(getUsuarioLogado)
  const errorMessage =
    getErrorMessage(error) || 'Erro não especificado, por favor entre em contato com o suporte'
  const shouldShowAgendaProvaDetranGo =
    matricula?.uf === 'GO' &&
    Number(matricula.cursoId) === 1 &&
    getErrorMessage(error).toUpperCase().includes('PROVA NAO ENCONTRADA')

  function handleOpenSuporte() {
    jivo.openJivoChat({
      customData: [
        { title: 'Local', content: 'Certificado' },
        { title: 'Motivo', content: 'Erro ao emitir certificado' },
        { title: 'Erro', content: errorMessage },
      ],
    })
  }

  function handleOpenWhatsapp() {
    window.open(
      getCertificadoWhatsappUrl({ matricula, errorMessage, usuarioLogado }),
      '_blank',
      'noopener,noreferrer'
    )
  }

  return (
    <CourseStatusCard tone="error">
      <Title>Não foi possível emitir o certificado.</Title>
      <Description>
        Encontramos um problema ao validar as informações necessárias para liberar o certificado.
      </Description>

      <CertificadoErroReasonBox>
        <IonIcon icon={warningOutline} />
        <span>Motivo:</span>
        <strong>{errorMessage}</strong>
      </CertificadoErroReasonBox>

      <Show condition={shouldShowAgendaProvaDetranGo}>
        <AgendaProvaDetranGo />
      </Show>

      <CertificadoErroSupportBox>
        <button className="chat" type="button" onClick={handleOpenSuporte}>
          ABRIR CHAT ONLINE COM O SUPORTE
          <IonIcon icon={arrowUpOutline} />
        </button>
        <button className="whatsapp" type="button" onClick={handleOpenWhatsapp}>
          Suporte pelo Whatsapp
          <IonIcon icon={logoWhatsapp} />
        </button>
      </CertificadoErroSupportBox>
    </CourseStatusCard>
  )
}

function AgendaProvaDetranGo() {
  return (
    <CertificadoErroAgendaText>
      <p>
        Para finalizar o processo de reciclagem, você precisa agendar sua prova teórica
        presencialmente em uma unidade <strong>Vapt Vupt, DETRAN-GO ou CIRETRAN</strong> do estado de
        Goiás.
      </p>

      <p>Procure a unidade mais próxima e solicite o agendamento da prova.</p>
    </CertificadoErroAgendaText>
  )
}

function ProjetoPedagogico({ matricula }: { matricula: MatriculaModel }) {
  if (!matricula?.cursoId) return null
  const cursoId = matricula.cursoId

  const [{ data: dataProjetoPedagogico }, fetchProjetoPedagogico] = useAxios(
    {
      url: `/app/downloads/curso/${cursoId}`,
      params: { tipoDocumento: 'PROJETO_PEDAGOGICO' },
    },
    { manual: true }
  )

  useEffect(() => {
    fetchProjetoPedagogico()
  }, [cursoId])

  return (
    <>
      {dataProjetoPedagogico?.map((arquivo) => {
        return <ArquivoProjetoPedagogico key={arquivo.id ?? arquivo.link} arquivo={arquivo} />
      })}
    </>
  )
}

function ArquivoProjetoPedagogico({ arquivo }: { arquivo: DownloadModel }) {
  const linkArquivo = useDecorateBlobWithSas({ blobPath: arquivo.link })

  function handleClickDownload() {
    window.open(linkArquivo)
  }

  return (
    <CertificadoPrimaryButton type="button" onClick={handleClickDownload}>
      <DownloadSvg width={23} height={23} />
      {arquivo.nome}
    </CertificadoPrimaryButton>
  )
}

interface CertificadoPrazoPendenteInfo {
  totalDias: number
  diaAtual: number
  diasRestantes: number
  percentual: number
  previsaoLiberacao: string
}

function CertificadoAguardandoPrazoCard({ prazo }: { prazo: CertificadoPrazoPendenteInfo }) {
  const { Title, Description, Panel, PanelHeader, ProgressRow, ProgressTrack, Footnote } =
    CourseStatusCard
  const { ProgressFill, EmissionText, PredictionText } = CertificadoAguardandoPrazoCard
  const diasRestantesTexto =
    prazo.diasRestantes === 1 ? 'Falta 1 dia' : `Faltam ${prazo.diasRestantes} dias`

  return (
    <CourseStatusCard tone="info">
      <Title>Parabéns! Você concluiu todas as atividades do curso.</Title>
      <Description>
        Agora estamos aguardando o cumprimento do prazo mínimo exigido pelos órgãos de trânsito para
        emissão do seu certificado.
      </Description>

      <Panel>
        <PanelHeader>Progresso para emissão do certificado</PanelHeader>
        <ProgressRow>
          <ProgressTrack>
            <ProgressFill style={{ width: `${prazo.percentual}%` }} />
          </ProgressTrack>
          <span>
            Dia {prazo.diaAtual} de {prazo.totalDias}
          </span>
        </ProgressRow>
        <EmissionText>{diasRestantesTexto}</EmissionText>
        <PredictionText>Previsão de liberação: {prazo.previsaoLiberacao}</PredictionText>
      </Panel>

      <Footnote>
        Mesmo após concluir todas as atividades, existe um prazo mínimo obrigatório de realização do
        curso, que deve ser respeitado antes da emissão do certificado e do envio das informações
        para a base nacional e para o seu prontuário. Essa medida garante a validade legal da
        certificação perante os órgãos fiscalizadores.
      </Footnote>
    </CourseStatusCard>
  )
}

function getErrorMessage(error: AxiosError<any>) {
  const data = error.response?.data
  if (typeof data === 'string') return data
  return data?.message ?? ''
}

function normalizeMessage(message: string) {
  return message
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function getPrazoPendenteKind(error: AxiosError<any>) {
  const message = normalizeMessage(getErrorMessage(error))
  if (message.includes('certificado sera emitido daqui')) return 'CERTIFICADO'
  if (message.includes('curso so pode ser finalizado apos')) return 'FINALIZACAO'
  return null
}

function getDiasRestantesFromError(error: AxiosError<any>) {
  const match = normalizeMessage(getErrorMessage(error)).match(/(?:daqui|apos)\s+(\d+)/)
  if (!match?.[1]) return null
  return Number(match[1])
}

function getTotalDiasPrazo({
  curso,
  diasRestantes,
}: {
  curso?: CursoModel
  diasRestantes: number
}) {
  const diasCurso = Number(curso?.diasParaEmissaoCertificado) || 0
  return Math.max(diasCurso, diasRestantes)
}

function getPrazoPendenteInfo({
  curso,
  error,
}: {
  curso?: CursoModel
  error: AxiosError<any>
}): CertificadoPrazoPendenteInfo | null {
  const kind = getPrazoPendenteKind(error)
  if (!kind) return null

  const diasRestantes = getDiasRestantesFromError(error)
  if (diasRestantes === null || Number.isNaN(diasRestantes)) return null

  const totalDias = getTotalDiasPrazo({ curso, diasRestantes })
  const diaAtual = Math.min(Math.max(totalDias - diasRestantes, 0), totalDias)
  const percentual = totalDias
    ? Math.min(Math.max(Math.round((diaAtual / totalDias) * 100), 0), 100)
    : 0
  const previsaoLiberacao = format(addDays(new Date(), diasRestantes), 'dd/MM/yyyy')

  return {
    totalDias,
    diaAtual,
    diasRestantes,
    percentual,
    previsaoLiberacao,
  }
}

CertificadoAguardandoPrazoCard.ProgressFill = styled(CourseStatusCard.ProgressFill)`
  background: ${COURSE_STATUS_CARD_COLORS.info};
`
CertificadoAguardandoPrazoCard.EmissionText = styled.div`
  margin: 8px 0 0;
  color: ${AUTH_COLORS.blue};
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
`
CertificadoAguardandoPrazoCard.PredictionText = styled.div`
  margin: 2px 0 0;
  color: rgba(130, 130, 130, 1);
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
`

export function AguardandoEnvioCertificado() {
  return <CertificadoEmProcessamentoCard />
}

function CertificadoEmProcessamentoCard() {
  const { Title, Description, Panel, PanelHeader, ProgressRow, ProgressTrack, ProgressFill } =
    CourseStatusCard
  const { SupportText } = CertificadoEmProcessamentoCard
  const jivo = useOpenJivoChat()

  function handleOpenSuporte() {
    jivo.openJivoChat({})
  }

  return (
    <CourseStatusCard tone="success">
      <Title>Parabéns pela sua conquista!</Title>
      <Description>
        Você concluiu todas as etapas do curso e cumpriu o prazo mínimo exigido para certificação.
      </Description>

      <Panel>
        <PanelHeader>Certificado em processamento</PanelHeader>
        <ProgressRow>
          <ProgressTrack>
            <ProgressFill />
          </ProgressTrack>
          <span>100%</span>
        </ProgressRow>
        <p>Seu certificado será registrado junto ao DETRAN e em breve estará disponível.</p>
      </Panel>

      <SupportText>
        Caso tenha urgência na emissão do certificado,{' '}
        <a onClick={handleOpenSuporte}>entre em contato com o suporte clicando aqui</a>
      </SupportText>
    </CourseStatusCard>
  )
}

CertificadoEmProcessamentoCard.SupportText = styled.p`
  margin: 14px 0 0;
  color: rgba(18, 42, 65, 0.56);
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;

  a {
    color: ${AUTH_COLORS.orange};
    cursor: pointer;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`
